import { useCurrentUser } from "@src/context/UserContext";
import {
  useMutation,
  useQueryClient,
  useQuery as useReactQuery,
} from "@tanstack/react-query";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";

const accounts = {
  useQueryAll,
  useQueryAllMap,
  mutations: {
    usePatchName,
  },
};

export default accounts;

export const ACCOUNTS_KEY = "backpackSdk/accounts";

export type Account = ClientOutput["accounts"]["Account"];
type AccountWithAudit = ClientOutput["accounts"]["AccountWithAudit"];

type UpdateAccountNameRequest = {
  accountId: number;
  name: string;
};

async function getAccounts(): Promise<Account[]> {
  const { data, error } = await FetchClients.accounts.GET("", {}); // Manually overridden from "/" in scripts/openapi/accounts.json
  if (data) return data;
  throw new Error(error);
}

async function patchAccountName({
  accountId,
  name,
}: UpdateAccountNameRequest): Promise<AccountWithAudit> {
  const { data, error } = await FetchClients.accounts.PATCH(
    "/{account_id}/name",
    {
      params: { path: { account_id: accountId } },
      body: { name },
    }
  );

  if (data) return data;
  if (isBackpackResponseErrorBody(error)) {
    throw new BackpackServiceError(error);
  }
  throw new Error(
    typeof error === "string" ? error : "Failed to update account name"
  );
}

function useQueryAll() {
  // Accounts returned are determined by current signed-in user, so cache should break when user changes
  const { id } = useCurrentUser();

  return useReactQuery({
    queryKey: [ACCOUNTS_KEY, id],
    queryFn: () => getAccounts(),
    select: (data) => [...data].sort((a, b) => a.name.localeCompare(b.name)),
    refetchOnMount: false,
    staleTime: 60000,
  });
}

function usePatchName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: patchAccountName,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ACCOUNTS_KEY],
      });
    },
  });
}

const accountSummariesMapAtom = atom<Map<number, Account> | undefined>(
  undefined
);
// Memoizes result globally and reuses requests made by useQueryAll
function useQueryAllMap() {
  const [accountSummariesMap, setAccountSummariesMap] = useAtom(
    accountSummariesMapAtom
  );
  const queryAccountSummaries = useQueryAll();

  useEffect(() => {
    setAccountSummariesMap(
      new Map(
        queryAccountSummaries.data?.map((account) => [account.id, account]) ??
          []
      )
    );
  }, [queryAccountSummaries.data, setAccountSummariesMap]);

  return {
    ...queryAccountSummaries,
    data: accountSummariesMap,
  };
}
